import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@material-ui/core';
import BipsEditor, { IBipsValue } from './BipsEditor';
import { IReferralAgreementData } from './ReferralAgreementForm';

export interface IEntityConfig {
  id?: string;
  familyEntityId: string;
  excluded: boolean;
  tiered: boolean;
  flatBips: number;
  tiers: { min: number; max: number | null; bips: number }[];
  exclusionReason?: string | null;
}

export interface IFamilyEntity {
  id: string;
  name: string;
  type: 'MERCHANT' | 'PARENT' | 'GRANDPARENT';
  parentId?: string;
  parentName?: string;
  grandparentId?: string;
  grandparentName?: string;
}

export interface IInheritedFrom {
  label: string;
  source: string;
}

interface IEntModalProps {
  entity: IFamilyEntity;
  agreement: IReferralAgreementData;
  existingConfig: IEntityConfig | null;
  ancestorExclusion: string | null;
  inheritedFrom: IInheritedFrom;
  onClose: () => void;
  onSave: (config: IEntityConfig) => Promise<void>;
}

const SCOPE_LABELS: Record<string, string> = {
  MERCHANT: 'Merchant',
  PARENT: 'Parent',
  GRANDPARENT: 'Grandparent',
};

const EntModal: React.FC<IEntModalProps> = ({
  entity,
  agreement,
  existingConfig,
  ancestorExclusion,
  inheritedFrom,
  onClose,
  onSave,
}) => {
  const fallback: IEntityConfig = {
    familyEntityId: entity.id,
    excluded: false,
    tiered: agreement.tiered ?? false,
    flatBips: agreement.bips ?? 0,
    tiers: (agreement.tiers ?? []).map((t) => ({ ...t, max: t.max ?? null })),
    exclusionReason: null,
  };

  const [cfg, setCfg] = useState<IEntityConfig>(existingConfig ?? fallback);
  const [isSaving, setIsSaving] = useState(false);

  const s = (patch: Partial<IEntityConfig>) => setCfg((c) => ({ ...c, ...patch }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(cfg);
      onClose();
    } catch {
      // error toast shown by caller
    } finally {
      setIsSaving(false);
    }
  };

  const bipsValue: IBipsValue = {
    tiered: cfg.tiered,
    flatBips: cfg.flatBips,
    tiers: cfg.tiers.map((t) => ({ ...t, max: t.max ?? '' })),
  };

  const handleBipsChange = (v: IBipsValue) => {
    s({
      tiered: v.tiered,
      flatBips: v.flatBips,
      tiers: v.tiers.map((t) => ({ ...t, max: t.max === '' ? null : Number(t.max) })),
    });
  };

  return (
    <Dialog
      aria-labelledby="ent-modal-title"
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open
    >
      <DialogTitle id="ent-modal-title">
        <div className="ent-modal-title">
          <span className={`et-badge et-badge--${entity.type.toLowerCase()}`}>
            {SCOPE_LABELS[entity.type]}
          </span>
          <div>
            <div className="ent-modal-name">{entity.name}</div>
            {entity.type === 'MERCHANT' && entity.parentName && (
              <div className="ent-modal-sub">
                Under
                {' '}
                {entity.parentName}
                {entity.grandparentName ? ` → ${entity.grandparentName}` : ''}
              </div>
            )}
            {entity.type === 'PARENT' && entity.grandparentName && (
              <div className="ent-modal-sub">
                Under
                {' '}
                {entity.grandparentName}
              </div>
            )}
          </div>
        </div>
      </DialogTitle>

      <DialogContent>
        {/* Priority hierarchy chain */}
        <div className="ent-priority-chain">
          <div className="ent-priority-chain-label">BiPs Priority Chain</div>
          <div className="ent-priority-steps">
            <span className="ent-priority-step ent-priority-step--active">
              {entity.name}
            </span>
            {entity.type === 'MERCHANT' && (
              <>
                <span className="ent-priority-arrow">→</span>
                <span className="ent-priority-step">
                  {entity.parentName ?? 'Parent'}
                </span>
              </>
            )}
            {entity.type !== 'GRANDPARENT' && (
              <>
                <span className="ent-priority-arrow">→</span>
                <span className="ent-priority-step">
                  {entity.grandparentName ?? 'Grandparent'}
                </span>
              </>
            )}
            <span className="ent-priority-arrow">→</span>
            <span className="ent-priority-step">APM Default</span>
          </div>
        </div>

        {/* Inherited / fallback info */}
        <div className="ent-modal-fallback-info">
          <span>
            <span className="ent-modal-fallback-bold">Overriding:</span>
            {' '}
            {inheritedFrom.source}
            {' '}
            (
            {inheritedFrom.label}
            )
          </span>
          <span className="ent-modal-fallback-note">
            {' · '}
            {entity.name}
            {entity.type === 'MERCHANT' && entity.parentName ? ` → ${entity.parentName}` : ''}
            {entity.grandparentName ? ` → ${entity.grandparentName}` : ''}
            {' → APM'}
          </span>
        </div>

        {/* Ancestor exclusion warning */}
        {ancestorExclusion && (
          <div className="ent-modal-warning">
            <span className="ent-modal-warning-icon">⚠</span>
            <div>
              <div className="ent-modal-warning-title">Inherited Exclusion</div>
              <div>
                {ancestorExclusion}
                . Individual inclusion is locked while the ancestor is excluded.
              </div>
            </div>
          </div>
        )}

        {/* Exclude toggle */}
        <div className={`ent-modal-excl-row${cfg.excluded || ancestorExclusion ? ' ent-modal-excl-row--active' : ''}`}>
          <div>
            <div className="ent-modal-excl-title">Exclude from Referral Billing</div>
            <div className="ent-modal-excl-sub">
              Prevent
              {' '}
              {entity.name}
              {' '}
              from referral charges
            </div>
            {existingConfig?.excluded && existingConfig.exclusionReason && (
              <div className="ent-modal-excl-note">
                Reason:
                {' '}
                {existingConfig.exclusionReason}
              </div>
            )}
          </div>
          {ancestorExclusion ? (
            <span
              className="ent-modal-locked"
              title={ancestorExclusion}
            >
              Locked
            </span>
          ) : (
            <input
              aria-label="Exclude from referral billing"
              checked={cfg.excluded}
              onChange={(e) => s({ excluded: e.target.checked })}
              type="checkbox"
            />
          )}
        </div>

        {/* Exclusion reason — only shown when entity is being excluded */}
        {cfg.excluded && !ancestorExclusion && (
          <div className="apm-modal-field">
            <div className="apm-modal-label">Exclusion Reason (optional)</div>
            <textarea
              aria-label="Exclusion reason"
              className="ent-modal-reason-input"
              onChange={(e) => s({ exclusionReason: e.target.value })}
              placeholder="e.g. Contract expired, entity not eligible for referral billing…"
              rows={2}
              value={cfg.exclusionReason ?? ''}
            />
            <div className="ent-modal-reason-hint">
              Stored for audit trail. Visible to all admins.
            </div>
          </div>
        )}

        {/* BiPs configuration — hidden when excluded */}
        {!cfg.excluded && (
          <div className="apm-modal-field">
            <div className="apm-modal-bips-title">BiPs Override</div>
            <div className="ent-modal-bips-hint">
              Leave unchanged to inherit
              {' '}
              {inheritedFrom.source}
              {' '}
              (
              {inheritedFrom.label}
              ). Set a custom rate below to override.
            </div>
            <BipsEditor
              invoicingCurrency={agreement.invoicingCurrency ?? 'USD'}
              onChange={handleBipsChange}
              value={bipsValue}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <button
          className="btn btn-outline"
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={isSaving}
          onClick={handleSave}
          type="button"
        >
          {isSaving ? (
            <>
              <CircularProgress
                size={16}
                style={{ color: '#fff', marginRight: 6 }}
              />
              Saving...
            </>
          ) : 'Save Config'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default EntModal;
